import { useMemo, useState } from 'react';
import { mergeBoardPdf } from '../../api/boardContent';
import { BoardItem, FolderTreeNode } from '../../types/boardContent';
import './MergePdfModal.css';

import { reportError } from '../../utils/notify';
interface MergePdfModalProps {
  boardId: number;
  rootFolders: FolderTreeNode[];
  onClose: () => void;
  onDone: (created: BoardItem) => void;
}

function MergePdfModal({ boardId, rootFolders, onClose, onDone }: MergePdfModalProps) {
  const [selected, setSelected] = useState<number[]>([]);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');

  const selectedNames = useMemo(
    () =>
      selected
        .map((id) => rootFolders.find((folder) => folder.folderId === id)?.folderName)
        .filter(Boolean) as string[],
    [selected, rootFolders]
  );

  const toggle = (folderId: number) => {
    setSelected((prev) => {
      if (prev.includes(folderId)) {
        return prev.filter((id) => id !== folderId);
      }
      if (prev.length >= 2) {
        return [prev[1], folderId];
      }
      return [...prev, folderId];
    });
    setError('');
  };

  const handleSubmit = async () => {
    if (selected.length !== 2) {
      reportError(setError, '폴더를 정확히 2개 선택해 주세요. (예: 공통 + KS)');
      return;
    }
    try {
      setRunning(true);
      setError('');
      const created = await mergeBoardPdf(boardId, selected);
      onDone(created);
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        '통합 PDF 생성에 실패했습니다. Gotenberg 실행 여부를 확인해 주세요.';
      reportError(setError, message);
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="merge-pdf-modal__backdrop" role="presentation" onClick={onClose}>
      <div
        className="merge-pdf-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="merge-pdf-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="merge-pdf-modal__header">
          <div>
            <h3 id="merge-pdf-title">통합 PDF 생성</h3>
            <p>최상위 폴더 2개를 선택하면 트리 순서대로 변환·병합 후 보드 최상단에 저장됩니다.</p>
          </div>
          <button type="button" className="merge-pdf-modal__close" onClick={onClose}>
            닫기
          </button>
        </header>

        <div className="merge-pdf-modal__body">
          {rootFolders.length === 0 ? (
            <p className="merge-pdf-modal__hint">선택할 최상위 폴더가 없습니다.</p>
          ) : (
            <ul className="merge-pdf-modal__list">
              {rootFolders.map((folder) => {
                const checked = selected.includes(folder.folderId);
                return (
                  <li key={folder.folderId}>
                    <label className={checked ? 'is-checked' : ''}>
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={running}
                        onChange={() => toggle(folder.folderId)}
                      />
                      <span>{folder.folderName}</span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}

          <p className="merge-pdf-modal__hint">
            예: 공통 + KS, 공통 + 단체표준 · 선택 순서대로 병합됩니다
            {selectedNames.length > 0 ? ` (현재: ${selectedNames.join(' → ')})` : ''}
          </p>

          {error && <p className="merge-pdf-modal__error">{error}</p>}
        </div>

        <footer className="merge-pdf-modal__footer">
          <button type="button" onClick={onClose} disabled={running}>
            취소
          </button>
          <button
            type="button"
            className="is-primary"
            disabled={running || selected.length !== 2}
            onClick={() => void handleSubmit()}
          >
            {running ? '생성 중... (수 분 소요될 수 있음)' : '통합 PDF 생성'}
          </button>
        </footer>
      </div>
    </div>
  );
}

export default MergePdfModal;
